import React, { useState, useEffect, useRef } from 'react';

const ColumnConfiguration = ({
  columns = [],
  visibleColumns = [],
  onColumnChange,
  open = false,
  onClose,
  anchorEl = null
}) => {
  const [localVisibleColumns, setLocalVisibleColumns] = useState(visibleColumns);
  const [selectAll, setSelectAll] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setLocalVisibleColumns(visibleColumns);
    setSelectAll(columns.length > 0 && visibleColumns.length === columns.length);
  }, [visibleColumns, columns]);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      onClose();
    }
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    setLocalVisibleColumns(checked ? columns.map(col => col.id) : []);
  };

  const handleColumnToggle = (columnId) => {
    setLocalVisibleColumns(prev => {
      const newColumns = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      setSelectAll(newColumns.length === columns.length);
      return newColumns;
    });
  };

  const handleApply = () => {
    onColumnChange(localVisibleColumns);
    onClose();
  };

  const handleClearAll = () => {
    setLocalVisibleColumns([]);
    setSelectAll(false);
  };

  const isIndeterminate =
    localVisibleColumns.length > 0 && localVisibleColumns.length < columns.length;

  if (!open) return null;

  const getDropdownStyle = () => {
    if (!anchorEl) return { position: 'fixed', top: '50px', right: '0px', zIndex: 1300 };

    const rect = anchorEl.getBoundingClientRect();
    const dropdownWidth = 250;
    const dropdownHeight = 300;
    const gap = 8;
    const viewportHeight = window.innerHeight;

    let top = rect.bottom + gap;
    if (rect.bottom + gap + dropdownHeight > viewportHeight) {
      top = rect.top - dropdownHeight - gap;
    }
    const left = rect.right - dropdownWidth;

    return { position: 'fixed', top: `${top}px`, left: `${left}px`, zIndex: 1300 };
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        ...getDropdownStyle(),
        width: '250px',
        maxHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      }}
    >
      {/* Header */}
      <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0', background: '#f8f9fa' }}>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>Column Configuration</div>
      </div>

      {/* Column List */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {/* Select All */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #f0f0f0',
            background: '#fafafa',
            cursor: 'pointer',
          }}
          onClick={() => handleSelectAll({ target: { checked: !selectAll } })}
        >
          <div style={{
            width: 18, height: 18, border: '2px solid var(--forbrand-color)', borderRadius: 3,
            marginRight: 12, position: 'relative',
            backgroundColor: selectAll || isIndeterminate ? 'var(--forbrand-color)' : 'white',
          }}>
            {selectAll && (
              <div style={{
                position: 'absolute', left: 3, top: 0, width: 6, height: 10,
                border: 'solid white', borderWidth: '0 2px 2px 0', transform: 'rotate(45deg)',
              }} />
            )}
            {isIndeterminate && !selectAll && (
              <div style={{ position: 'absolute', left: 3, top: 7, width: 8, height: 2, background: 'white' }} />
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>All Columns</div>
        </div>

        {/* Individual Columns */}
        {columns.map((column) => (
          <div
            key={column.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
              cursor: 'pointer',
            }}
            onClick={() => handleColumnToggle(column.id)}
          >
            <div style={{
              width: 18, height: 18, border: '2px solid var(--forbrand-color)', borderRadius: 3,
              marginRight: 12, position: 'relative',
              backgroundColor: localVisibleColumns.includes(column.id) ? 'var(--forbrand-color)' : 'white',
            }}>
              {localVisibleColumns.includes(column.id) && (
                <div style={{
                  position: 'absolute', left: 3, top: 0, width: 6, height: 10,
                  border: 'solid white', borderWidth: '0 2px 2px 0', transform: 'rotate(45deg)',
                }} />
              )}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>{column.label}</div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderTop: '1px solid #e0e0e0',
        background: '#f8f9fa',
      }}>
        <button
          onClick={handleClearAll}
          style={{ background: 'none', border: 'none', color: '#666', fontSize: 14, cursor: 'pointer' }}
        >
          Clear All
        </button>
        <button
          onClick={handleApply}
          style={{ background: 'var(--forbrand-color)', color: '#fff', border: 'none', fontSize: 14, padding: '6px 16px', borderRadius: 4, cursor: 'pointer' }}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default ColumnConfiguration;
